import React, { useState, useEffect, useMemo, useCallback } from 'react';
import API from '../services/api';
import { formatDate } from '../dateUtils';

/* ============================================================
   Styles — kept in-file as a single object so this file remains
   drop-in. No external CSS deps.
   ============================================================ */
const S = {
  page: {
    margin: '2rem auto',
    maxWidth: 1280,
    padding: '0 1.5rem',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    color: 'var(--text-strong)',
  },
  h1: {
    fontSize: '1.6rem',
    fontWeight: 600,
    margin: '0 0 0.25rem',
    letterSpacing: '-0.02em',
  },
  subtle: { color: 'var(--text-muted)', fontSize: '0.9rem' },

  card: {
    background: '#ffffff',
    border: '1px solid var(--border-color)',
    borderRadius: 10,
    padding: '1.25rem 1.5rem',
    marginBottom: '1.25rem',
    boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
  },
  cardTitle: {
    fontSize: '0.85rem',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    color: 'var(--text-strong)',
    margin: '0 0 0.9rem',
  },

  filterRow: {
    display: 'grid',
    gridTemplateColumns: '2fr 1fr 1fr auto auto',
    gap: '0.75rem',
    alignItems: 'end',
  },
  field: { display: 'flex', flexDirection: 'column', gap: '0.25rem' },
  label: { fontSize: '0.78rem', fontWeight: 500, color: 'var(--text-strong)' },
  input: {
    padding: '0.5rem 0.65rem',
    border: '1px solid var(--border-color)',
    borderRadius: 6,
    fontSize: '0.9rem',
    background: '#fff',
    fontFamily: 'inherit',
  },

  btn: {
    padding: '0.5rem 0.95rem',
    border: '1px solid var(--border-color)',
    borderRadius: 6,
    background: '#fff',
    color: 'var(--text-strong)',
    fontSize: '0.875rem',
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'background 0.12s, border-color 0.12s',
    fontFamily: 'inherit',
  },
  btnPrimary: {
    background: 'var(--text-strong)',
    color: '#fff',
    borderColor: 'var(--text-strong)',
  },
  btnDanger: {
    background: '#fff',
    color: '#b91c1c',
    borderColor: '#fecaca',
  },
  btnGhost: {
    background: 'transparent',
    border: '1px solid transparent',
    color: 'var(--text-strong)',
    padding: '0.3rem 0.55rem',
    fontSize: '0.82rem',
  },

  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '0.75rem',
  },
  stat: {
    padding: '0.85rem 1rem',
    border: '1px solid var(--border-color)',
    borderRadius: 8,
    background: 'var(--surface-muted)',
  },
  statLabel: {
    fontSize: '0.72rem',
    color: 'var(--text-muted)',
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    marginBottom: 4,
  },
  statValue: { fontSize: '1.25rem', fontWeight: 600, color: '#0f172a' },
  statValueAccent: { fontSize: '1.25rem', fontWeight: 600, color: '#047857' },
  statValueNeg: { fontSize: '1.25rem', fontWeight: 600, color: '#b91c1c' },

  table: {
    width: '100%',
    borderCollapse: 'separate',
    borderSpacing: 0,
    fontSize: '0.88rem',
  },
  th: {
    textAlign: 'left',
    padding: '0.55rem 0.75rem',
    fontWeight: 600,
    color: 'var(--text-strong)',
    fontSize: '0.76rem',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    borderBottom: '1px solid var(--border-color)',
    background: '#f9fafb',
  },
  td: {
    padding: '0.55rem 0.75rem',
    borderBottom: '1px solid var(--surface-muted)',
    verticalAlign: 'middle',
  },
  tdRight: { textAlign: 'right', fontVariantNumeric: 'tabular-nums' },

  pill: {
    display: 'inline-block',
    padding: '0.12rem 0.5rem',
    borderRadius: 999,
    fontSize: '0.72rem',
    fontWeight: 600,
    letterSpacing: '0.02em',
  },
  pillSession:  { background: '#eff6ff', color: '#1d4ed8' },
  pillManual:   { background: '#ecfeff', color: '#0e7490' },
  pillBonus:    { background: '#ecfdf5', color: '#047857' },
  pillDeduction:{ background: '#fef2f2', color: '#b91c1c' },
  pillLegacy:   { background: '#f5f3ff', color: '#6d28d9' },

  empty: {
    padding: '1.5rem',
    textAlign: 'center',
    color: '#94a3b8',
    fontStyle: 'italic',
    border: '1px dashed var(--border-color)',
    borderRadius: 8,
  },

  adjForm: {
    display: 'grid',
    gridTemplateColumns: '120px 110px 100px 1fr auto',
    gap: '0.5rem',
    alignItems: 'end',
  },

  badge: {
    display: 'inline-block',
    padding: '0.1rem 0.45rem',
    background: 'var(--surface-muted)',
    color: 'var(--text-strong)',
    borderRadius: 4,
    fontSize: '0.72rem',
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
  },
};

const ADJ_TYPE_META = {
  SESSION:        { label: 'Session',      style: S.pillSession },
  MANUAL_SESSION: { label: 'Manual',       style: S.pillManual },
  BONUS:          { label: 'Bonus',        style: S.pillBonus },
  DEDUCTION:      { label: 'Deduction',    style: S.pillDeduction },
  LEGACY_BONUS:   { label: 'Weekly Bonus', style: S.pillLegacy },
};

const fmtMoney = (n) => `$${(parseFloat(n) || 0).toFixed(2)}`;
const fmtDate = (d) => (d ? formatDate(d) : '');

/* ============================================================ */

function AdminPayrollPage() {
  // ---- filters
  const [therapistId, setTherapistId] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // ---- preview state
  const [preview, setPreview] = useState(null);
  // shape: { sessions, paySplit, subtotals, totalCollected, therapistPay }

  // ---- adjustment draft
  const [adjDraft, setAdjDraft] = useState({
    adjType: 'BONUS',
    adjDate: '',
    amount: '',
    description: '',
    clientName: '',
  });

  // ---- saved payrolls + therapists
  const [payrolls, setPayrolls] = useState([]);
  const [therapists, setTherapists] = useState([]);
  const [busy, setBusy] = useState(false);
  const [notes, setNotes] = useState('');

  /* ----------------- initial load ----------------- */
  useEffect(() => {
    fetchTherapists();
    fetchPayrolls();
  }, []);

  // Convenience: when both dates set, default the adjustment date to startDate.
  // We intentionally only depend on startDate here — re-running this when
  // adjDraft.adjDate changes would fight the user's own edits.
  useEffect(() => {
    if (startDate && !adjDraft.adjDate) {
      setAdjDraft(d => ({ ...d, adjDate: startDate }));
    }
  }, [startDate]); // adjDraft.adjDate intentionally omitted

  async function fetchTherapists() {
    try {
      const res = await API.get('/admin/users');
      setTherapists(res.data.filter(u => u.role === 'THERAPIST'));
    } catch (e) { console.error(e); }
  }

  async function fetchPayrolls() {
    try {
      const res = await API.get('/admin/payroll');
      setPayrolls(res.data);
    } catch (e) { console.error(e); }
  }

  /* ----------------- preview ----------------- */
  const previewPayroll = useCallback(async () => {
    if (!therapistId || !startDate || !endDate) {
      alert('Pick a therapist and a date range first.');
      return;
    }
    setBusy(true);
    try {
      const sesRes = await API.get('/admin/payroll/sessions', {
        params: { therapistId, startDate, endDate },
      });
      setPreview(sesRes.data);
    } catch (e) {
      console.error(e);
      alert('Error loading preview.');
    } finally {
      setBusy(false);
    }
  }, [therapistId, startDate, endDate]);

  // Auto-preview whenever the filters change and are all set
  useEffect(() => {
    if (therapistId && startDate && endDate) previewPayroll();
    else setPreview(null);
  }, [therapistId, startDate, endDate, previewPayroll]);

  /* ----------------- adjustment CRUD ----------------- */
  async function addAdjustment() {
    if (!therapistId) { alert('Pick a therapist first.'); return; }
    if (!adjDraft.adjDate || !adjDraft.amount) {
      alert('Adjustment needs a date and an amount.');
      return;
    }
    try {
      await API.post('/admin/payroll/adjustments', {
        therapistId,
        ...adjDraft,
      });
      setAdjDraft(d => ({
        ...d,
        amount: '',
        description: '',
        clientName: '',
      }));
      previewPayroll();
    } catch (e) {
      console.error(e);
      alert(e?.response?.data?.message || 'Error adding adjustment.');
    }
  }

  async function deleteAdjustment(id) {
    if (!window.confirm('Remove this adjustment?')) return;
    try {
      await API.delete(`/admin/payroll/adjustments/${id}`);
      previewPayroll();
    } catch (e) {
      alert(e?.response?.data?.message || 'Error removing adjustment.');
    }
  }

  /* ----------------- generate / approve / delete ----------------- */
  async function generatePayroll() {
    if (!therapistId || !startDate || !endDate) {
      alert('Pick a therapist and date range.');
      return;
    }
    if (!preview || preview.sessions.length === 0) {
      if (!window.confirm('No sessions or adjustments in this range. Generate anyway?')) return;
    }
    setBusy(true);
    try {
      const res = await API.post('/admin/payroll/generate', {
        therapistId, startDate, endDate, notes,
      });
      alert(
        `Payroll #${res.data.payrollId} created.\n` +
        `Total collected: ${fmtMoney(res.data.totalCollected)}\n` +
        `Therapist pay:   ${fmtMoney(res.data.therapistPay)}`
      );
      setNotes('');
      fetchPayrolls();
      previewPayroll();
    } catch (e) {
      console.error(e);
      alert('Error generating payroll.');
    } finally {
      setBusy(false);
    }
  }

  async function approvePayroll(id) {
    try {
      await API.put(`/admin/payroll/approve/${id}`);
      fetchPayrolls();
    } catch (e) { alert('Error approving payroll.'); }
  }

  async function deletePayroll(id) {
    if (!window.confirm('Delete this payroll? Its adjustments will be released for re-use.')) return;
    try {
      await API.delete(`/admin/payroll/${id}`);
      fetchPayrolls();
      previewPayroll();
    } catch (e) { alert('Error deleting payroll.'); }
  }

  async function exportPayroll(p) {
    // Download via axios so the JWT auth header travels with the request,
    // then trigger a save with a blob URL. `window.open` doesn't carry auth.
    try {
      const res = await API.get(`/admin/payroll/${p.id}/export`, {
        responseType: 'blob',
      });
      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const filename =
        `paystub_${p.last_name}_${p.start_date}_to_${p.end_date}.xlsx`;
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
    } catch (e) {
      console.error(e);
      alert('Error downloading pay stub.');
    }
  }

  /* ----------------- derived values ----------------- */
  const selectedTherapist = useMemo(
    () => therapists.find(t => String(t.id) === String(therapistId)),
    [therapists, therapistId]
  );

  const sessions = preview?.sessions || [];
  const subtotals = preview?.subtotals || {};

  /* ============================================================ */
  return (
    <div style={S.page}>
      <div style={{ marginBottom: '1.25rem' }}>
        <h1 style={S.h1}>Payroll</h1>
        <div style={S.subtle}>
          Preview, adjust, and generate pay periods for each therapist.
        </div>
      </div>

      {/* ============= FILTER BAR ============= */}
      <div style={S.card}>
        <div style={S.filterRow}>
          <div style={S.field}>
            <label style={S.label}>Therapist</label>
            <select
              style={S.input}
              value={therapistId}
              onChange={(e) => setTherapistId(e.target.value)}
            >
              <option value="">-- Select therapist --</option>
              {therapists.map(t => (
                <option key={t.id} value={t.id}>
                  {t.first_name} {t.last_name}
                </option>
              ))}
            </select>
          </div>
          <div style={S.field}>
            <label style={S.label}>Start date</label>
            <input
              type="date" style={S.input}
              value={startDate} onChange={e => setStartDate(e.target.value)}
            />
          </div>
          <div style={S.field}>
            <label style={S.label}>End date</label>
            <input
              type="date" style={S.input}
              value={endDate} onChange={e => setEndDate(e.target.value)}
            />
          </div>
          <button
            style={S.btn}
            onClick={previewPayroll}
            disabled={busy}
          >
            Refresh
          </button>
          <button
            style={{ ...S.btn, ...S.btnPrimary }}
            onClick={generatePayroll}
            disabled={busy || !preview}
            title={!preview ? 'Preview the range first' : 'Save this payroll'}
          >
            Generate payroll
          </button>
        </div>
        {selectedTherapist && preview && (
          <div style={{ ...S.subtle, marginTop: '0.65rem' }}>
            Pay split for {selectedTherapist.first_name} {selectedTherapist.last_name}:
            <span style={{ ...S.badge, marginLeft: 6 }}>
              {parseFloat(preview.paySplit).toFixed(1)}%
            </span>
          </div>
        )}
      </div>

      {/* ============= LIVE TOTALS ============= */}
      {preview && (
        <div style={S.card}>
          <h3 style={S.cardTitle}>This pay period</h3>
          <div style={S.statsGrid}>
            <div style={S.stat}>
              <div style={S.statLabel}>Sessions collected</div>
              <div style={S.statValue}>{fmtMoney(subtotals.sessions)}</div>
            </div>
            <div style={S.stat}>
              <div style={S.statLabel}>Manual + bonuses</div>
              <div style={S.statValue}>
                {fmtMoney((subtotals.manualSessions || 0) + (subtotals.bonuses || 0))}
              </div>
            </div>
            <div style={S.stat}>
              <div style={S.statLabel}>Deductions</div>
              <div style={subtotals.deductions > 0 ? S.statValueNeg : S.statValue}>
                {subtotals.deductions > 0 ? '−' : ''}{fmtMoney(subtotals.deductions)}
              </div>
            </div>
            <div style={S.stat}>
              <div style={S.statLabel}>Therapist pay</div>
              <div style={S.statValueAccent}>
                {fmtMoney(preview.therapistPay)}
              </div>
            </div>
          </div>
          <div style={{ ...S.subtle, marginTop: '0.85rem', fontSize: '0.82rem' }}>
            Total collected (logs + manual + bonuses − deductions):{' '}
            <strong>{fmtMoney(preview.totalCollected)}</strong>
          </div>
        </div>
      )}

      {/* ============= ADD ADJUSTMENT ============= */}
      {therapistId && (
        <div style={S.card}>
          <h3 style={S.cardTitle}>Add adjustment</h3>
          <div style={S.adjForm}>
            <div style={S.field}>
              <label style={S.label}>Type</label>
              <select
                style={S.input}
                value={adjDraft.adjType}
                onChange={(e) => setAdjDraft(d => ({ ...d, adjType: e.target.value }))}
              >
                <option value="BONUS">Bonus</option>
                <option value="DEDUCTION">Deduction</option>
                <option value="MANUAL_SESSION">Manual session</option>
              </select>
            </div>
            <div style={S.field}>
              <label style={S.label}>Date</label>
              <input
                type="date" style={S.input}
                value={adjDraft.adjDate}
                onChange={e => setAdjDraft(d => ({ ...d, adjDate: e.target.value }))}
              />
            </div>
            <div style={S.field}>
              <label style={S.label}>Amount</label>
              <input
                type="number" step="0.01" placeholder="0.00"
                style={S.input}
                value={adjDraft.amount}
                onChange={e => setAdjDraft(d => ({ ...d, amount: e.target.value }))}
              />
            </div>
            <div style={S.field}>
              <label style={S.label}>
                {adjDraft.adjType === 'MANUAL_SESSION' ? 'Client / description' : 'Description'}
              </label>
              <input
                type="text"
                placeholder={
                  adjDraft.adjType === 'MANUAL_SESSION'
                    ? 'Client name or reason for manual entry'
                    : 'What is this for?'
                }
                style={S.input}
                value={
                  adjDraft.adjType === 'MANUAL_SESSION'
                    ? adjDraft.clientName
                    : adjDraft.description
                }
                onChange={e => setAdjDraft(d => (
                  adjDraft.adjType === 'MANUAL_SESSION'
                    ? { ...d, clientName: e.target.value }
                    : { ...d, description: e.target.value }
                ))}
              />
            </div>
            <button
              style={{ ...S.btn, ...S.btnPrimary }}
              onClick={addAdjustment}
            >
              Add
            </button>
          </div>
          <div style={{ ...S.subtle, fontSize: '0.78rem', marginTop: '0.6rem' }}>
            Bonuses and manual sessions credit the therapist; deductions subtract.
            Manual sessions follow the pay-split rule; bonuses and deductions don't.
          </div>
        </div>
      )}

      {/* ============= PREVIEW TABLE ============= */}
      {preview && (
        <div style={S.card}>
          <h3 style={S.cardTitle}>Preview ({sessions.length} line items)</h3>
          {sessions.length === 0 ? (
            <div style={S.empty}>No sessions or adjustments in this range yet.</div>
          ) : (
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={S.th}>Date</th>
                  <th style={S.th}>Type</th>
                  <th style={S.th}>Client / Description</th>
                  <th style={S.th}>Service</th>
                  <th style={{ ...S.th, textAlign: 'right' }}>Billed</th>
                  <th style={{ ...S.th, textAlign: 'right' }}>Amount</th>
                  <th style={S.th}></th>
                </tr>
              </thead>
              <tbody>
                {sessions.map((s, idx) => {
                  const meta = ADJ_TYPE_META[s.record_type] || ADJ_TYPE_META.SESSION;
                  const isDeduction = s.record_type === 'DEDUCTION';
                  const isAdjustment = !!s.adjustment_id;
                  return (
                    <tr key={`${s.record_type}-${s.daily_log_id || s.adjustment_id || idx}`}>
                      <td style={S.td}>{fmtDate(s.session_date)}</td>
                      <td style={S.td}>
                        <span style={{ ...S.pill, ...meta.style }}>{meta.label}</span>
                      </td>
                      <td style={S.td}>
                        {s.client_name || s.description || '—'}
                        {s.description && s.client_name ? (
                          <div style={{ ...S.subtle, fontSize: '0.75rem' }}>{s.description}</div>
                        ) : null}
                      </td>
                      <td style={S.td}>{s.service_type || '—'}</td>
                      <td style={{ ...S.td, ...S.tdRight }}>
                        {parseFloat(s.billed_amount) ? fmtMoney(s.billed_amount) : '—'}
                      </td>
                      <td style={{
                        ...S.td, ...S.tdRight,
                        color: isDeduction ? '#b91c1c' : '#0f172a',
                        fontWeight: 500,
                      }}>
                        {isDeduction ? '−' : ''}{fmtMoney(s.collected_amount)}
                      </td>
                      <td style={S.td}>
                        {isAdjustment && (
                          <button
                            style={S.btnGhost}
                            onClick={() => deleteAdjustment(s.adjustment_id)}
                            title="Remove adjustment"
                          >
                            ✕
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}

          {/* Notes box for payroll record */}
          <div style={{ marginTop: '1rem' }}>
            <label style={S.label}>Notes (optional, saved to payroll record)</label>
            <textarea
              style={{ ...S.input, width: '100%', minHeight: 50, marginTop: 4 }}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. catch-up week, retro adjustment for Jan 28 missed entry, etc."
            />
          </div>
        </div>
      )}

      {/* ============= EXISTING PAYROLLS ============= */}
      <div style={S.card}>
        <h3 style={S.cardTitle}>Existing payroll records</h3>
        {payrolls.length === 0 ? (
          <div style={S.empty}>No payrolls generated yet.</div>
        ) : (
          <table style={S.table}>
            <thead>
              <tr>
                <th style={S.th}>#</th>
                <th style={S.th}>Therapist</th>
                <th style={S.th}>Period</th>
                <th style={{ ...S.th, textAlign: 'right' }}>Sessions</th>
                <th style={{ ...S.th, textAlign: 'right' }}>Bonuses</th>
                <th style={{ ...S.th, textAlign: 'right' }}>Deductions</th>
                <th style={{ ...S.th, textAlign: 'right' }}>Total</th>
                <th style={{ ...S.th, textAlign: 'right' }}>Pay</th>
                <th style={S.th}>Approved</th>
                <th style={S.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {payrolls.map(p => (
                <tr key={p.id}>
                  <td style={S.td}><span style={S.badge}>#{p.id}</span></td>
                  <td style={S.td}>{p.first_name} {p.last_name}</td>
                  <td style={S.td}>
                    {fmtDate(p.start_date)} – {fmtDate(p.end_date)}
                  </td>
                  <td style={{ ...S.td, ...S.tdRight }}>
                    {fmtMoney(p.sessions_subtotal)}
                  </td>
                  <td style={{ ...S.td, ...S.tdRight }}>
                    {fmtMoney(p.bonuses_subtotal)}
                  </td>
                  <td style={{
                    ...S.td, ...S.tdRight,
                    color: parseFloat(p.deductions_subtotal) > 0 ? '#b91c1c' : '#0f172a',
                  }}>
                    {parseFloat(p.deductions_subtotal) > 0 ? '−' : ''}
                    {fmtMoney(p.deductions_subtotal)}
                  </td>
                  <td style={{ ...S.td, ...S.tdRight, fontWeight: 500 }}>
                    {fmtMoney(p.total_collected)}
                  </td>
                  <td style={{ ...S.td, ...S.tdRight, fontWeight: 600, color: '#047857' }}>
                    {fmtMoney(p.therapist_pay)}
                  </td>
                  <td style={S.td}>
                    {p.approved_at ? (
                      <div style={{ fontSize: '0.78rem' }}>
                        <div>{p.approver_first_name || ''} {p.approver_last_name || ''}</div>
                        <div style={S.subtle}>
                          {formatDate(p.approved_at)}
                        </div>
                      </div>
                    ) : (
                      <span style={{ ...S.pill, ...S.pillDeduction }}>Pending</span>
                    )}
                  </td>
                  <td style={S.td}>
                    <div style={{ display: 'flex', gap: '0.25rem', flexWrap: 'wrap' }}>
                      {!p.approved_at && (
                        <button
                          style={{ ...S.btn, ...S.btnPrimary }}
                          onClick={() => approvePayroll(p.id)}
                        >Approve</button>
                      )}
                      <button
                        style={S.btn}
                        onClick={() => exportPayroll(p)}
                        title="Download pay stub as Excel"
                      >Export</button>
                      <button
                        style={{ ...S.btn, ...S.btnDanger }}
                        onClick={() => deletePayroll(p.id)}
                      >Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default AdminPayrollPage;
